import get from 'lodash.get';
import { PERIOD, ALL_PERIODS, BM_PERIODS } from '../../constants/enrollment';
import { getBenchmarkWindowByDate } from './assessmentWindowsUtils';
import {
    calculateBurstAvailability,
    isMeasureAvailableForPlacementTest,
} from './calculateBurstAvailability';
import {
    DIBELS_AVAILABILITY,
    MEASURE_BY_MEASURE_SCORE,
    SUPPORTED_GRADES,
} from '../../constants/assessments';

/**
 * Calculates number of dibels results per period. PM period converted to PM according to PM result date
 * @param {Array<Object>} bmWindows bm windows to get period dates
 * @param student- student to get results for
 * @param gatedMeasuresStatuses - gated measure statuses by period
 * @param dibelsResults - list of dibels results
 * @returns {Object} object with measure results count per period
 * {
 *     {measure}: {
 *         {period}: number
 *     }
 * }
 */
const getDibelsResultsCountForStudent = (
    bmWindows,
    student,
    gatedMeasuresStatuses,
    dibelsResults,
) =>
    ALL_PERIODS.reduce((resultsCount, period) => {
        const studentResults = get(dibelsResults, `${period}.${student.id}`, []).filter(
            result => result.isValid,
        );
        const statuses = get(gatedMeasuresStatuses, `${period}`, {});
        const statusList = Object.keys(statuses).map(measure => ({
            ...statuses[measure],
            measure: MEASURE_BY_MEASURE_SCORE[measure] || measure,
        }));
        return {
            ...resultsCount,
            ...[...studentResults, ...statusList].reduce((completeResultCount, result) => {
                let bmPeriod = period;
                if (period === PERIOD.PM) {
                    const resultCreateDate = result.clientCreateDate;
                    const resultDay = new Date(resultCreateDate);
                    resultDay.setHours(0, 0, 0, 0);
                    bmPeriod = getBenchmarkWindowByDate(bmWindows, resultDay).period;
                }
                const resultCount = get(completeResultCount, `${result.measure}.${bmPeriod}`, 0);
                return {
                    ...completeResultCount,
                    [result.measure]: {
                        ...completeResultCount[result.measure],
                        [bmPeriod]: resultCount + 1,
                    },
                };
            }, resultsCount),
        };
    }, {});

/**
 * Calculates number of burst results per period. PM result is associated with BM period by result date
 * @param {Array<Object>} bmWindows bm windows to get period dates
 * @param studentId - student id to get results for
 * @param burstResults - list of burst results
 * @returns {Object} object with measure results count per period
 * {
 *     {measure}: {
 *         {period}: number
 *     }
 * }
 */
const getBurstResultsCountForStudent = (bmWindows, studentId, burstResults) =>
    ALL_PERIODS.reduce((resultsCount, period) => {
        const studentResults = get(burstResults, `${period}.${studentId}`, []);
        return {
            ...resultsCount,
            ...studentResults.reduce(
                (completeResultCount, result) => ({
                    ...completeResultCount,
                    ...result.results.reduce((probeResultsCount, probeResult) => {
                        let bmPeriod = period;
                        if (period === PERIOD.PM) {
                            const resultCreateDate = probeResult.clientCreateDate;
                            const resultDay = new Date(resultCreateDate);
                            resultDay.setHours(0, 0, 0, 0);
                            bmPeriod = getBenchmarkWindowByDate(bmWindows, resultDay).period;
                        }
                        const resultCount = get(
                            probeResultsCount,
                            `${probeResult.measure}.${bmPeriod}`,
                            0,
                        );
                        return {
                            ...probeResultsCount,
                            [probeResult.measure]: {
                                ...probeResultsCount[probeResult.measure],
                                [bmPeriod]: resultCount + 1,
                            },
                        };
                    }, completeResultCount),
                }),
                resultsCount,
            ),
        };
    }, {});

/**
 * Calculates list of measures required by placement test
 * @param {Array<Object>} bmWindows bm windows to get period dates
 * @param currentPeriod - current period
 * @param studentInstructionStatus - group last instruction status with date for placement test status calculation
 * @param gatedMeasuresStatuses - gated measure statuses by period
 * @param student - student
 * @param burstResults - object with all burst results
 * @param dibelsResults - object with of all dibels results
 * @returns {null|Array} null if student grade is not supported or list of measures
 */
export const getRequiredPlacementTestMeasures = (
    bmWindows,
    currentPeriod,
    studentInstructionStatus,
    gatedMeasuresStatuses,
    student,
    burstResults,
    dibelsResults,
) => {
    if (!SUPPORTED_GRADES.includes(student.grade)) {
        return null;
    }
    const dibelsMeasureAvailability = DIBELS_AVAILABILITY[student.grade];
    const burstMeasureAvailability = calculateBurstAvailability(
        student.id,
        student.grade,
        gatedMeasuresStatuses,
        burstResults,
        dibelsResults,
    );
    const dibelsResultsCount = getDibelsResultsCountForStudent(
        bmWindows,
        student,
        gatedMeasuresStatuses,
        dibelsResults,
    );
    const burstResultsCount = getBurstResultsCountForStudent(bmWindows, student.id, burstResults);
    const measureAvailability = BM_PERIODS.reduce(
        (measuresByPeriod, period) => ({
            ...measuresByPeriod,
            [period]: {
                ...dibelsMeasureAvailability[period],
                ...burstMeasureAvailability[period],
            },
        }),
        {},
    );
    const measureResultCount = {
        ...dibelsResultsCount,
        ...burstResultsCount,
    };
    const requiredMeasures = BM_PERIODS.reduce((measuresByPeriod, period) => {
        const measureAvailabilityByMeasure = measureAvailability[period];
        return {
            ...measuresByPeriod,
            [period]: Object.keys(measureAvailabilityByMeasure).reduce((measures, measure) => {
                if (isMeasureAvailableForPlacementTest(measureAvailabilityByMeasure, measure)) {
                    const resultCount = get(measureResultCount, `${measure}.${period}`, 0);
                    if (resultCount === 0) {
                        return [...measures, measure];
                    }
                }
                return measures;
            }, []),
        };
    }, {});
    if (
        BM_PERIODS.some(period => {
            return requiredMeasures[period].length === 0;
        })
    ) {
        return [];
    }
    if (studentInstructionStatus === undefined) {
        return requiredMeasures[currentPeriod];
    }
    const instructionCreateDate = studentInstructionStatus.instruction_create_date;
    const targetDate = new Date(instructionCreateDate);
    targetDate.setHours(0, 0, 0, 0);
    const targetPeriod = getBenchmarkWindowByDate(bmWindows, targetDate).period;
    const targetPeriodOrdinal = BM_PERIODS.indexOf(targetPeriod);
    const currentPeriodOrdinal = BM_PERIODS.indexOf(currentPeriod);
    // if current period is higher than group creation period and student has incomplete placement test status
    // it's required for student to complete assessments required by group creation period
    // if they weren't passed in current of higher periods
    if (targetPeriodOrdinal < currentPeriodOrdinal) {
        const searchingPeriods = BM_PERIODS.slice(targetPeriodOrdinal);
        const measureAvailabilityByMeasure = measureAvailability[targetPeriod];
        return Object.keys(measureAvailabilityByMeasure).reduce((measures, measure) => {
            if (isMeasureAvailableForPlacementTest(measureAvailabilityByMeasure, measure)) {
                const hasResults = searchingPeriods.some(period => {
                    return get(measureResultCount, `${measure}.${period}`, 0) > 0;
                });
                if (!hasResults) {
                    return [...measures, measure];
                }
            }
            return measures;
        }, []);
    }
    // If student has incomplete placement test status and current period is the same as group creation period
    // it's required for student to complete assessments required by group creation period
    // if they weren't passed in current period
    return requiredMeasures[currentPeriod];
};

export default getRequiredPlacementTestMeasures;
